import { TimePicker as MuiTimePicker, TimePickerProps } from '@mui/x-date-pickers/TimePicker';
import { PickersShortcutsItem } from '@mui/x-date-pickers/PickersShortcuts';
import { parseShorthandTime } from '@/common/application/utils/timeUtils';
import { set } from 'date-fns';
import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
import { Box, Tooltip, IconButton, InputAdornment } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTimePickerShorthand } from './useTimePickerShorthand';

export type CustomTimePickerProps = TimePickerProps<Date> & {
  fullWidth?: boolean;
  helperText?: ReactNode;
  id?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error?: boolean;
  required?: boolean;
  /**
   * Enable shorthand time input parsing (e.g., "5a" → 5:00 AM, "2p" → 2:00 PM).
   * When disabled, the TimePicker behaves as a standard MUI TimePicker.
   * @default true
   */
  enableShorthand?: boolean;
};

type AmPm = 'a' | 'p';

interface ShorthandData {
  digits: string;
  ampm: AmPm | null;
}

/**
 * TimePicker component with shorthand input support and time shortcuts.
 *
 * Features:
 * - Shorthand time input (e.g., "8p" → 8:00 PM, "730a" → 7:30 AM) - parses on Tab key
 * - If no AM/PM specified, keeps current AM/PM or defaults to AM
 * - Predefined time shortcuts (Now, Morning, Noon, Afternoon, Evening, Midnight)
 * - Selects all text on focus for easy rewriting
 */
const TimePicker: React.FC<CustomTimePickerProps> = ({
  fullWidth = false,
  helperText,
  id,
  onBlur,
  error,
  enableShorthand = true,
  ...rest
}) => {
  // ============================================================================
  // Global Shorthand State
  // ============================================================================
  const { isShorthandEnabled: globalShorthandEnabled } = useTimePickerShorthand();
  // Use prop if provided, otherwise use global state
  // If prop is explicitly set, use it AND global state (both must be true)
  // If prop is undefined, use only global state
  const isShorthandEnabled = (() => {
    if (enableShorthand === undefined) {
      return globalShorthandEnabled;
    }
    return enableShorthand && globalShorthandEnabled;
  })();

  // ============================================================================
  // Refs
  // ============================================================================
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rawKeystrokesRef = useRef<string>('');
  const onChangeRef = useRef(rest.onChange);
  const hasParsedShorthandRef = useRef<boolean>(false);
  const currentAmPmRef = useRef<AmPm | null>(null);

  // Keep onChange ref updated to avoid stale closures
  useEffect(() => {
    onChangeRef.current = rest.onChange;
  }, [rest.onChange]);

  // ============================================================================
  // Time Shortcuts
  // ============================================================================
  const timeShortcuts = useMemo<PickersShortcutsItem<Date | null>[]>(() => {
    const baseDate = rest.value instanceof Date ? rest.value : new Date();
    const now = new Date();

    const shortcuts: PickersShortcutsItem<Date | null>[] = [
      { label: 'Now', getValue: () => now },
      { label: 'Morning (9 AM)', getValue: () => set(baseDate, { hours: 9, minutes: 0, seconds: 0, milliseconds: 0 }) },
      { label: 'Noon (12 PM)', getValue: () => set(baseDate, { hours: 12, minutes: 0, seconds: 0, milliseconds: 0 }) },
      { label: 'Afternoon (3 PM)', getValue: () => set(baseDate, { hours: 15, minutes: 0, seconds: 0, milliseconds: 0 }) },
      { label: 'Evening (6 PM)', getValue: () => set(baseDate, { hours: 18, minutes: 0, seconds: 0, milliseconds: 0 }) },
      { label: 'Midnight (12 AM)', getValue: () => set(baseDate, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }) }
    ];

    const existingShortcuts = rest.slotProps?.shortcuts;
    if (existingShortcuts && typeof existingShortcuts === 'object' && 'items' in existingShortcuts) {
      return [...shortcuts, ...((existingShortcuts.items as PickersShortcutsItem<Date | null>[]) ?? [])];
    }
    return shortcuts;
  }, [rest.value, rest.slotProps?.shortcuts]);

  // ============================================================================
  // Shorthand Parsing System
  // ============================================================================
  useEffect(() => {
    // Only set up shorthand parsing if enabled
    if (!isShorthandEnabled || !containerRef.current) return;

    /**
     * Extracts AM/PM from a string value.
     */
    const getAmPmFromValue = (value: string): AmPm | null => {
      const match = /([ap])m?/i.exec(value.trim());
      return match?.[1] ? (match[1].toLowerCase() as AmPm) : null;
    };

    /**
     * Extracts shorthand data (digits + AM/PM) from a string.
     * Handles formats like: "5a", "730a", "5:00 AM", "05:00 AM", or just "5"
     */
    const extractShorthand = (value: string): ShorthandData | null => {
      const trimmed = value.trim();
      if (!trimmed) return null;

      // Pattern 1: Direct shorthand "5a", "730a", "12p"
      let match = /^(\d{1,4})\s*([ap])(m?)$/i.exec(trimmed);
      if (match?.[1] && match[2]) {
        return { digits: match[1], ampm: match[2].toLowerCase() as AmPm };
      }

      // Pattern 2: Formatted time "5:00 AM", "05:00 AM"
      match = /(\d{1,2}):\d{2}\s*([ap])/i.exec(trimmed);
      if (match?.[1] && match[2]) {
        const hour = Number.parseInt(match[1], 10);
        if (hour >= 1 && hour <= 12) {
          return { digits: hour.toString(), ampm: match[2].toLowerCase() as AmPm };
        }
      }

      // Pattern 3: Just digits "5", "730"
      match = /^(\d{1,4})$/.exec(trimmed);
      if (match?.[1]) {
        return { digits: match[1], ampm: null };
      }

      return null;
    };

    /**
     * Parses and applies shorthand time input.
     * Tries multiple sources: raw keystrokes, input value, etc.
     * Returns true if shorthand was successfully parsed and applied.
     */
    const parseAndApplyShorthand = (input: HTMLInputElement): boolean => {
      let shorthandData: ShorthandData | null = null;

      // Try 1: Extract from raw keystrokes
      if (rawKeystrokesRef.current) {
        shorthandData = extractShorthand(rawKeystrokesRef.current);
      }

      // Try 2: Extract from input value
      if (!shorthandData && input.value) {
        shorthandData = extractShorthand(input.value);
      }

      // If we have digits, construct final shorthand with AM/PM
      if (shorthandData?.digits) {
        // Use extracted AM/PM, or current AM/PM, or default to 'a'
        const ampm = shorthandData.ampm || currentAmPmRef.current || 'a';
        const shorthandInput = `${shorthandData.digits}${ampm}`;

        const parsedTime = parseShorthandTime(shorthandInput, new Date());
        if (parsedTime && onChangeRef.current) {
          // Update current AM/PM for next time
          currentAmPmRef.current = ampm;

          // Apply the parsed time
          onChangeRef.current(parsedTime, { validationError: null });

          // Clear tracking
          rawKeystrokesRef.current = '';

          return true; // Successfully parsed
        }
      }

      return false; // No shorthand parsed
    };

    /**
     * Handles keydown events to capture keystrokes and parse on Tab.
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (!target || !containerRef.current?.contains(target) || target.tagName !== 'INPUT') {
        return;
      }

      const input = target as HTMLInputElement;

      // Capture keystrokes
      if (event.key.length === 1 && /[0-9apAP]/.test(event.key)) {
        rawKeystrokesRef.current += event.key.toLowerCase();
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        rawKeystrokesRef.current = rawKeystrokesRef.current.slice(0, -1);
      }

      // Parse on Tab
      if (event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();

        // Parse shorthand
        const wasParsed = parseAndApplyShorthand(input);

        // Set flag BEFORE moving focus so blur can check it
        if (wasParsed) {
          hasParsedShorthandRef.current = true;
        }

        // Move focus to next element after a small delay
        // This ensures the flag is set before blur fires
        const moveFocusToNext = () => {
          if (input.form) {
            const formElements = Array.from(input.form.elements) as HTMLElement[];
            const currentIndex = formElements.indexOf(input);
            const nextElement = formElements[currentIndex + 1] as HTMLElement | undefined;
            if (nextElement && ['INPUT', 'TEXTAREA', 'BUTTON'].includes(nextElement.tagName)) {
              nextElement.focus();
            } else {
              input.blur();
            }
          } else {
            input.blur();
          }
          // Reset flag after blur has fired
          setTimeout(() => {
            hasParsedShorthandRef.current = false;
          }, 50);
        };

        setTimeout(moveFocusToNext, 10);
      }
    };

    /**
     * Handles input events to track AM/PM from the input value.
     */
    const handleInput = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target?.tagName === 'INPUT' && containerRef.current?.contains(target)) {
        const ampm = getAmPmFromValue(target.value || '');
        if (ampm) {
          currentAmPmRef.current = ampm;
        }
      }
    };

    /**
     * Handles focus events to reset state and extract current AM/PM.
     */
    const handleFocus = (event: FocusEvent) => {
      const target = event.target as HTMLInputElement;
      if (target?.tagName === 'INPUT' && containerRef.current?.contains(target)) {
        // Reset state
        rawKeystrokesRef.current = '';
        hasParsedShorthandRef.current = false;

        // Extract and store current AM/PM
        const ampm = getAmPmFromValue(target.value || '');
        if (ampm) {
          currentAmPmRef.current = ampm;
        }

        // Select all text for easy rewriting
        setTimeout(() => target.select(), 10);
      }
    };

    /**
     * Handles blur events as fallback to parse shorthand if Tab wasn't used.
     */
    const handleBlur = (event: FocusEvent) => {
      const target = event.target as HTMLInputElement;
      if (target?.tagName === 'INPUT' && containerRef.current?.contains(target) && !hasParsedShorthandRef.current) {
        parseAndApplyShorthand(target);
        rawKeystrokesRef.current = '';
      }
    };

    // Add event listeners
    containerRef.current.addEventListener('keydown', handleKeyDown, true);
    containerRef.current.addEventListener('input', handleInput);
    containerRef.current.addEventListener('focusin', handleFocus);
    containerRef.current.addEventListener('focusout', handleBlur);

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('keydown', handleKeyDown, true);
        containerRef.current.removeEventListener('input', handleInput);
        containerRef.current.removeEventListener('focusin', handleFocus);
        containerRef.current.removeEventListener('focusout', handleBlur);
      }
    };
  }, [isShorthandEnabled]);

  // ============================================================================
  // Render
  // ============================================================================
  const controlledValue = rest.value === undefined ? null : rest.value;

  // If shorthand is disabled, render standard MUI TimePicker without any custom logic
  if (!isShorthandEnabled) {
    return (
      <MuiTimePicker
        {...rest}
        value={controlledValue}
        slotProps={{
          ...rest.slotProps,
          textField: {
            ...(rest.slotProps?.textField as Record<string, unknown>),
            id: id,
            fullWidth: fullWidth,
            error: error,
            helperText: helperText,
            onBlur: onBlur
          }
        }}
      />
    );
  }

  // Shorthand enabled - render with custom logic and icon indicator
  return (
    <Box ref={containerRef} data-timepicker-container>
      <MuiTimePicker
        {...rest}
        value={controlledValue}
        onChange={(date, context) => {
          rawKeystrokesRef.current = '';
          if (rest.onChange) {
            rest.onChange(date, context);
          }
        }}
        slotProps={{
          ...rest.slotProps,
          shortcuts: {
            ...(typeof rest.slotProps?.shortcuts === 'object' ? rest.slotProps.shortcuts : {}),
            items: timeShortcuts
          } as { items: PickersShortcutsItem<Date | null>[] },
          textField: {
            ...(rest.slotProps?.textField as Record<string, unknown>),
            id,
            fullWidth,
            error,
            helperText,
            onBlur,
            InputProps: {
              ...(rest.slotProps?.textField as { InputProps?: Record<string, unknown> } | undefined)?.InputProps,
              endAdornment: (
                <>
                  {isShorthandEnabled && (
                    <InputAdornment position="end">
                      <Tooltip
                        title="Shorthand enabled: Type '5a' or '2p' and press Tab to quickly set time. Toggle with Ctrl+Space."
                        arrow
                        placement="top"
                      >
                        <IconButton
                          size="small"
                          sx={{
                            color: 'warning.main',
                            padding: '4px',
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            }
                          }}
                          disableRipple
                          edge="end"
                        >
                          <AutoAwesomeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )}
                  {(rest.slotProps?.textField as { InputProps?: { endAdornment?: ReactNode } } | undefined)?.InputProps?.endAdornment ??
                    null}
                </>
              )
            }
          }
        }}
      />
    </Box>
  );
};

export default TimePicker;
